import {
  anonymousHubSession,
  hubItemDtoFromListing,
  memberHubSession,
  publicProfileFromHubProfile,
  type AdminUserInfo,
  type HubClient,
  type HubComment,
  type HubItemDto,
  type HubListing,
  type HubListingDetail,
  type HubPage,
  type HubProfile,
  type HubPublicProfileDto,
  type HubPublishInput,
  type HubQuery,
  type HubSessionDto,
} from "@roqeet/core";
import type { HubRepositorySet } from "./ports.js";

/**
 * Provider-neutral compatibility facade for UI/state code.
 *
 * UI code keeps depending on the core HubClient contract while provider
 * adapters supply repositories underneath. This keeps future storage/database
 * moves out of React views and state actions.
 */
export class RepositoryHubClient implements HubClient {
  constructor(private readonly repositories: HubRepositorySet) {}

  isConfigured(): boolean {
    return this.repositories.auth.isConfigured();
  }

  supportsLegacyDownloads(): boolean {
    return true;
  }

  currentProfile(): HubProfile | null {
    return this.repositories.auth.currentProfile();
  }

  getSession(): Promise<HubSessionDto> {
    const profile = this.currentProfile();
    return Promise.resolve(profile ? memberHubSession(publicProfileFromHubProfile(profile)) : anonymousHubSession());
  }

  signIn(idToken: string): Promise<HubProfile> {
    return this.repositories.auth.signIn(idToken);
  }

  signOut(): Promise<void> {
    return this.repositories.auth.signOut();
  }

  subscribe(onChange: (profile: HubProfile | null) => void): () => void {
    return this.repositories.auth.subscribe(onChange);
  }

  list(query: HubQuery): Promise<HubPage<HubListing>> {
    return this.repositories.projects.list(query);
  }

  async listHubItems(query: HubQuery): Promise<HubPage<HubItemDto>> {
    const page = await this.repositories.projects.list(query);
    return {
      items: page.items.map(hubItemDtoFromListing),
      ...(page.nextCursor ? { nextCursor: page.nextCursor } : {}),
    };
  }

  getHubItem(_idOrSlug: string): Promise<HubItemDto | null> {
    return Promise.resolve(null);
  }

  download(listingId: string): Promise<HubListingDetail> {
    return this.repositories.projects.download(listingId);
  }

  publish(input: HubPublishInput): Promise<HubListing> {
    return this.repositories.projects.publish(input);
  }

  unpublish(listingId: string): Promise<void> {
    return this.repositories.projects.unpublish(listingId);
  }

  getProfile(profileId: string): Promise<HubProfile | undefined> {
    return this.repositories.profiles.getProfile(profileId);
  }

  async getPublicProfile(idOrHandle: string): Promise<HubPublicProfileDto | null> {
    const profile =
      (await this.repositories.profiles.getProfile(idOrHandle))
      ?? (await this.repositories.profiles.getProfileByHandle(idOrHandle));
    return profile ? publicProfileFromHubProfile(profile) : null;
  }

  updateProfile(patch: Partial<Pick<HubProfile, "handle" | "bio">>): Promise<HubProfile> {
    return this.repositories.profiles.updateProfile(patch);
  }

  rate(listingId: string, stars: number): Promise<void> {
    return this.repositories.feedback.rate(listingId, stars);
  }

  listComments(listingId: string, cursor?: string): Promise<HubPage<HubComment>> {
    return this.repositories.feedback.listComments(listingId, cursor);
  }

  addComment(listingId: string, body: string): Promise<HubComment> {
    return this.repositories.feedback.addComment(listingId, body);
  }

  deleteComment(commentId: string): Promise<void> {
    return this.repositories.feedback.deleteComment(commentId);
  }

  follow(profileId: string): Promise<void> {
    return this.repositories.profiles.follow(profileId);
  }

  unfollow(profileId: string): Promise<void> {
    return this.repositories.profiles.unfollow(profileId);
  }

  updateListing(listingId: string, patch: Partial<HubPublishInput>): Promise<HubListing> {
    return this.repositories.projects.updateListing(listingId, patch);
  }

  bookmark(listingId: string): Promise<void> {
    return this.repositories.feedback.bookmark(listingId);
  }

  unbookmark(listingId: string): Promise<void> {
    return this.repositories.feedback.unbookmark(listingId);
  }

  incrementView(listingId: string): Promise<void> {
    return this.repositories.stats.incrementView(listingId);
  }

  getProfileByHandle(handle: string): Promise<HubProfile | undefined> {
    return this.repositories.profiles.getProfileByHandle(handle);
  }

  checkAdmin(): Promise<boolean> {
    return this.repositories.moderation.checkAdmin();
  }

  adminListUsers(limit: number, offset: number): Promise<AdminUserInfo[]> {
    return this.repositories.moderation.adminListUsers(limit, offset);
  }

  adminRemoveListing(listingId: string): Promise<void> {
    return this.repositories.moderation.adminRemoveListing(listingId);
  }
}

const DISABLED_MESSAGE =
  "RoQeet Hub is not configured. Local creation, import, and export remain available.";

function disabled(): Promise<never> {
  return Promise.reject(new Error(DISABLED_MESSAGE));
}

/**
 * Provider-neutral offline fallback used when no online Hub backend is
 * configured. It keeps local-first RoQeet runtime paths independent from any
 * legacy or future hosted provider.
 */
export class DisabledHubClient implements HubClient {
  isConfigured(): boolean {
    return false;
  }

  supportsLegacyDownloads(): boolean {
    return false;
  }

  currentProfile(): HubProfile | null {
    return null;
  }

  getSession(): Promise<HubSessionDto> {
    return Promise.resolve(anonymousHubSession());
  }

  signIn(_idToken: string): Promise<HubProfile> {
    return disabled();
  }

  signOut(): Promise<void> {
    return Promise.resolve();
  }

  subscribe(onChange: (profile: HubProfile | null) => void): () => void {
    onChange(null);
    return () => {};
  }

  list(_query: HubQuery): Promise<HubPage<HubListing>> {
    return Promise.resolve({ items: [] });
  }

  listHubItems(_query: HubQuery): Promise<HubPage<HubItemDto>> {
    return Promise.resolve({ items: [] });
  }

  getHubItem(_idOrSlug: string): Promise<HubItemDto | null> {
    return Promise.resolve(null);
  }

  download(_listingId: string): Promise<HubListingDetail> {
    return disabled();
  }

  publish(_input: HubPublishInput): Promise<HubListing> {
    return disabled();
  }

  unpublish(_listingId: string): Promise<void> {
    return disabled();
  }

  getProfile(_profileId: string): Promise<HubProfile | undefined> {
    return Promise.resolve(undefined);
  }

  getPublicProfile(_idOrHandle: string): Promise<HubPublicProfileDto | null> {
    return Promise.resolve(null);
  }

  updateProfile(_patch: Partial<Pick<HubProfile, "handle" | "bio">>): Promise<HubProfile> {
    return disabled();
  }

  rate(_listingId: string, _stars: number): Promise<void> {
    return disabled();
  }

  listComments(_listingId: string, _cursor?: string): Promise<HubPage<HubComment>> {
    return Promise.resolve({ items: [] });
  }

  addComment(_listingId: string, _body: string): Promise<HubComment> {
    return disabled();
  }

  deleteComment(_commentId: string): Promise<void> {
    return disabled();
  }

  follow(_profileId: string): Promise<void> {
    return disabled();
  }

  unfollow(_profileId: string): Promise<void> {
    return disabled();
  }

  updateListing(_listingId: string, _patch: Partial<HubPublishInput>): Promise<HubListing> {
    return disabled();
  }

  bookmark(_listingId: string): Promise<void> {
    return disabled();
  }

  unbookmark(_listingId: string): Promise<void> {
    return disabled();
  }

  incrementView(_listingId: string): Promise<void> {
    return Promise.resolve();
  }

  getProfileByHandle(_handle: string): Promise<HubProfile | undefined> {
    return Promise.resolve(undefined);
  }

  checkAdmin(): Promise<boolean> {
    return Promise.resolve(false);
  }

  adminListUsers(_limit: number, _offset: number): Promise<AdminUserInfo[]> {
    return disabled();
  }

  adminRemoveListing(_listingId: string): Promise<void> {
    return disabled();
  }
}
